/**
 * Accessible tabs: click or arrow keys (Left/Right/Home/End) switch the selected tab and
 * show its panel. Markup is rendered server-side with the first tab selected.
 */
class EbTabs extends HTMLElement {
  #abort = new AbortController();

  connectedCallback() {
    const { signal } = this.#abort;
    this.addEventListener('click', (event) => this.#onClick(event), { signal });
    this.addEventListener('keydown', (event) => this.#onKeydown(event), { signal });
  }

  disconnectedCallback() {
    this.#abort.abort();
    this.#abort = new AbortController();
  }

  get #tabs() {
    return /** @type {HTMLButtonElement[]} */ ([...this.querySelectorAll('[role="tab"]')]);
  }

  /** @param {HTMLButtonElement} tab */
  select(tab, focus = false) {
    for (const item of this.#tabs) {
      const selected = item === tab;
      item.setAttribute('aria-selected', String(selected));
      item.tabIndex = selected ? 0 : -1;
      const panelId = item.getAttribute('aria-controls');
      const panel = panelId ? document.getElementById(panelId) : null;
      if (panel) panel.hidden = !selected;
    }
    if (focus) tab.focus();
  }

  /** @param {MouseEvent} event */
  #onClick(event) {
    const tab = event.target instanceof Element ? event.target.closest('[role="tab"]') : null;
    if (tab instanceof HTMLButtonElement) this.select(tab);
  }

  /** @param {KeyboardEvent} event */
  #onKeydown(event) {
    const tabs = this.#tabs;
    const current = tabs.indexOf(/** @type {HTMLButtonElement} */ (document.activeElement));
    if (current === -1) return;
    const rtl = getComputedStyle(this).direction === 'rtl';
    let next = -1;
    if (event.key === (rtl ? 'ArrowLeft' : 'ArrowRight')) next = (current + 1) % tabs.length;
    else if (event.key === (rtl ? 'ArrowRight' : 'ArrowLeft')) next = (current - 1 + tabs.length) % tabs.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = tabs.length - 1;
    if (next === -1) return;
    event.preventDefault();
    const tab = tabs[next];
    if (tab) this.select(tab, true);
  }
}

if (!customElements.get('eb-tabs')) customElements.define('eb-tabs', EbTabs);
